import { apiInstance, gql } from "@/services/httpAdapter";

// Fragments
import {
	CallActivityPerformanceFragment,
	LongevityFragment,
	TopPerformerBonusFragment,
	UserFullNameFragment,
	InputValueFragment,
	ModelYearsFragment,
	CommissionTiersFragment,
	MetricFragment,
	VolumeBonusFragment,
	BonusDefinitionFragment,
	VariableOperationsGpsBonusSetupFragment,
} from "@PayPlans/api/fragments/commons";

import {
	DefaultCommissionsFragment,
	MiniDealFragment,
	ModelBasedFragment,
	DemoAllowanceFragment,
	PacksFragment,
	AutoAlertsFragment,
	CustomerSatisfactionFragment,
	DefaultCommissionsTierFragment,
	AdjustmentTierFragment,
} from "@PayPlans/api/fragments/salesAssociates";

// Retrieve sales pay plans list
export async function retrieveSalesPpList(locationId) {
	const query = gql`
		query getSalesPayPlans($locationId: String!) {
			getSalesPayPlans(locationId: $locationId) {
				_id
				name
				position
				locked
				available
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSalesPayPlans || [];
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Retrieve sales pay plans tree
export async function getSalesPayPlanSidebarTree(locationId) {
	const query = gql`
		query getSalesPayPlansSidebarTree($locationId: String!) {
			getSalesPayPlansSidebarTree(locationId: $locationId) {
				defaultPayPlans {
					defaultPayPlan {
						_id
						name
						position
						pdfAvailability
					}
					newVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
					preOwnedVehiclePayPlan {
						_id
						name
						position
						pdfAvailability
					}
				}
				available {
					_id
					name
					position
					pdfAvailability
				}
				inProgress {
					_id
					name
					position
					pdfAvailability
				}
				currentlyAssigned {
					_id
					name
					position
					pdfAvailability
				}
				previouslyAssigned {
					_id
					name
					position
					pdfAvailability
				}
			}
		}
	`;

	const variables = {
		locationId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSalesPayPlansSidebarTree || [];
	} catch (err) {
		console.error(err);
	}

	return [];
}

// Retrieve sales pay plans
export async function retrieveSalesPp(locationId, id) {
	const query = gql`
		query getSalesPayPlan($locationId: String!, $id: String!) {
			getSalesPayPlan(locationId: $locationId, id: $id) {
				_id
				locationId
				name
				position
				default {
					...DefaultCommissions
				}
				miniDeal {
					...MiniDeal
				}
				modelBased {
					...ModelBased
				}
				callActivityPerformance {
					...CallActivityPerformance
				}
				volumeBonus {
					...VolumeBonus
				}
				demoAllowance {
					...DemoAllowance
				}
				packs {
					...Packs
				}
				autoAlerts {
					...AutoAlerts
				}
				topSalesBonus {
					...TopPerformerBonus
				}
				customerSatisfaction {
					...CustomerSatisfaction
				}
				variableOperationsGpsBonus {
					...VariableOperationsGpsBonusSetup
				}
				longevity {
					...Longevity
				}
				locked
				available
				formatVersion
				createdAt
				createdBy {
					...UserFullName
				}
				updatedAt
				updatedBy {
					...UserFullName
				}
			}
		}
		${DefaultCommissionsFragment}
		${MiniDealFragment}
		${ModelBasedFragment}
		${CallActivityPerformanceFragment}
		${VolumeBonusFragment}
		${BonusDefinitionFragment}
		${DemoAllowanceFragment}
		${PacksFragment}
		${AutoAlertsFragment}
		${TopPerformerBonusFragment}
		${CustomerSatisfactionFragment}
		${LongevityFragment}
		${UserFullNameFragment}
		${InputValueFragment}
		${DefaultCommissionsTierFragment}
		${ModelYearsFragment}
		${AdjustmentTierFragment}
		${CommissionTiersFragment}
		${MetricFragment}
		${VariableOperationsGpsBonusSetupFragment}
	`;

	const variables = {
		locationId,
		id,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getSalesPayPlan;
	} catch (err) {
		console.error(err);
		return null;
	}
}

// Retrieve sales pay plans full and partial matches
export async function requestSalesPayPlanMatches(locationId, salesPayPlanId) {
	if (locationId && salesPayPlanId) {
		const query = gql`
			query getSalesPayPlanMatches($locationId: String!, $id: String!) {
				getSalesPayPlanMatches(id: $id, locationId: $locationId) {
					locationId
					payPlanId
					fullMatchList
					partialMatchList
				}
			}
		`;

		const variables = {
			id: salesPayPlanId,
			locationId,
		};

		try {
			const response = await apiInstance.post("/graphql", { query, variables });
			return response.data.data?.getSalesPayPlanMatches;
		} catch (err) {
			console.error(err);
			return null;
		}
	}

	return {
		locationId,
		payPlanId: salesPayPlanId,
		fullMatchList: [],
		partialMatchList: [],
	};
}
